import { useBooleanFlag } from '@library/hooks'
import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { assert } from '../../guards'
import type { Nullable } from '../../types'
import type { IAction, IEditAction } from './interfaces'

type ActionContextType<T> = {
  objects: T[]
  objectActions: IAction<T>[]
  sceneActions: IAction<T>[]
  editAction: Nullable<IEditAction>
  selectedObjectAction: Nullable<IAction<T>>
  selectedSceneAction: Nullable<IAction<T>>
  isSelectedAction: (actionTag: string) => boolean
  isEditingAction: boolean
  isLocked: boolean
  chooseAction: (actionTag: string) => void
  unselect: VoidFunction
  onResetAction: VoidFunction
  setLock: (isLocked: boolean) => void
}

const ActionContext = createContext<Nullable<ActionContextType<unknown>>>(null)

export const ActionContextProvider = <T,>({
  children,
  objectActions,
  editActions,
  sceneActions,
  objects,
  onActionEnd,
}: PropsWithChildren<{
  objectActions: IAction<T>[]
  sceneActions: IAction<T>[]
  editActions: IEditAction[]
  objects: T[]
  onActionEnd: VoidFunction
}>) => {
  const [selectedObjectAction, setSelectedObjectAction] = useState<Nullable<IAction<T>>>(null)
  const [selectedSceneAction, setSelectedSceneAction] = useState<Nullable<IAction<T>>>(null)
  const [editAction, setEditAction] = useState<Nullable<IEditAction<T>>>(null)
  const [isLocked, lockApi] = useBooleanFlag(false)

  const chooseAction = useCallback(
    (actionTag: string) => {
      if (isLocked) return

      setSelectedObjectAction(objectActions.find(action => action.tag === actionTag) ?? null)
      setSelectedSceneAction(sceneActions.find(action => action.tag === actionTag) ?? null)
    },
    [objectActions, sceneActions, isLocked],
  )

  const unselect = useCallback(() => {
    setSelectedSceneAction(null)
    setSelectedObjectAction(null)
    lockApi.setFalse()
  }, [lockApi])

  const onResetAction = useCallback(() => {
    onActionEnd()
    setEditAction(null)
  }, [])

  useEffect(() => {
    setEditAction(editActions.find(action => action.canApply()) ?? null)
  }, [editActions, objects])

  useEffect(() => {
    if (isLocked) return

    if (selectedObjectAction && !selectedObjectAction.canApply(objects)) {
      setSelectedObjectAction(null)
    }
  }, [objects, selectedObjectAction, isLocked])

  useEffect(() => {
    if (selectedSceneAction && !selectedSceneAction.canApply(objects)) {
      setSelectedSceneAction(null)
    }
  }, [objects, selectedSceneAction])

  const value = useMemo<ActionContextType<T>>(
    () => ({
      objects,
      objectActions,
      sceneActions,
      selectedObjectAction,
      selectedSceneAction,
      editAction,
      chooseAction,
      unselect,
      isSelectedAction: actionTag =>
        actionTag === selectedObjectAction?.tag || actionTag === selectedSceneAction?.tag,
      isEditingAction: !!editAction,
      onResetAction,
      isLocked,
      setLock: lockApi.setValue,
    }),
    [
      editAction,
      isLocked,
      objectActions,
      objects,
      sceneActions,
      selectedObjectAction,
      selectedSceneAction,
      chooseAction,
      unselect,
      onResetAction,
      lockApi.setValue,
    ],
  )

  return <ActionContext.Provider value={value}>{children}</ActionContext.Provider>
}

export const useActionContext = <T,>() => {
  const context = useContext(ActionContext) as Nullable<ActionContextType<T>>
  assert(context != null, 'Action context used without provider')
  return context
}
